import { peek } from '@art-suite/art-core-ts';
import { useScanEditingContext } from '@kaiizen/kai-pro-api-react-state';
import { type ChangeEvent, useCallback } from 'react';
import { useNavigate } from 'react-router';

import { useScanIsEditableCheck } from '@/Client/State';
import { useToast } from '@/Client/State/ToastContext';
import { PhotoDropZone } from '@/Client/Widgets/PhotoDropZone';
import { useBulkPhotoUploader } from '@/lib/photoLib';
import { HeroImageCard } from './HeroImageCard';

/**
 * Hero image for the current scan in editing context. Supports upload (append to scanRoot.photos)
 * and click-to-view (navigates to scan page with photo viewer). Use within ScanEditingContextProvider.
 */
export const EditableScanHeroImage = () => {
  const { scanId, scanRoot, scanIsEditable } = useScanEditingContext();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const checkEditableWithToast = useScanIsEditableCheck(showToast);
  const bulkPhotoUploader = useBulkPhotoUploader();

  const heroPhoto = peek(scanRoot?.photos);
  const heroPhotoId = heroPhoto?.photoId;

  const handleUpload = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!scanRoot || !checkEditableWithToast()) return;
      await bulkPhotoUploader(event.target.files, scanRoot, {});
      event.target.value = '';
    },
    [scanRoot, bulkPhotoUploader, checkEditableWithToast],
  );

  const handleClick = useCallback(() => {
    if (scanId && heroPhotoId) navigate(`/scan/${scanId}?photoId=${heroPhotoId}`);
  }, [scanId, heroPhotoId, navigate]);

  return (
    <PhotoDropZone scanPart={scanRoot} scanPartContext={{}}>
      <HeroImageCard
        scanId={scanId}
        hasUploadedHero={!!heroPhotoId}
        photoSource={heroPhoto?.photoSource}
        onPhotoClick={handleClick}
        onPhotoUpload={scanIsEditable ? handleUpload : undefined}
      />
    </PhotoDropZone>
  );
};
